import { PolicyFieldOption, PolicyNeedsReviewGroup } from "@swivl-mono/types";
import { useState } from "react";
import { FiAlertCircle } from "react-icons/fi";

interface NeedsReviewListProps {
  groups: PolicyNeedsReviewGroup[];
  busyGroupKeys: Set<string>;
  onFix: (group: PolicyNeedsReviewGroup, newValue: string) => void;
  /** Replaces "The document" in each card's sentence — for a caller whose source isn't a document
   * (e.g. "Your legacy system" for the Cortex migrator). Defaults to "The document". */
  sourceLabel?: string;
  /** Fixes ONE location within a `scope: "locations"` card independently of the rest — enables the
   * expandable per-location breakdown (NeedsReviewLocationBreakdown) below the card's bulk fix
   * control. Omitted = no breakdown, matching the original single-fix-for-everyone behavior
   * (bulk-import's own call site doesn't pass this). */
  onFixLocation?: (group: PolicyNeedsReviewGroup, locationId: number, newValue: string) => void;
  /** Makes the card go away without fixing it — sometimes the extraction is just wrong and there's
   * nothing to correct. Omitted = no Dismiss button, matching `onFixLocation`'s optionality
   * (bulk-import's own call site doesn't pass this either). */
  onDismiss?: (group: PolicyNeedsReviewGroup) => void;
}

/**
 * "N field(s) need your input" — a policyBuilder finding the scan extracted but couldn't normalize
 * into an accepted answer (see PolicyNeedsReviewGroup's doc comment: grouped by mapKey + rawExtracted,
 * so every location that hit the identical unrecognized text is fixed in one card). Fixing one is a
 * plain updatePolicyFinding call with the operator's chosen value — no separate "promote" step; once
 * it normalizes, the server drops it from policyNeedsReview and it shows up as an ordinary update on
 * the next refresh.
 */
export function NeedsReviewList({
  groups,
  busyGroupKeys,
  onFix,
  sourceLabel,
  onFixLocation,
  onDismiss
}: NeedsReviewListProps): React.ReactElement | null {
  if (groups.length === 0) return null;

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex items-baseline gap-2">
        <span className="text-[11px] font-bold tracking-[0.06em] text-red-700 uppercase">Needs your input</span>
        <span className="text-[12px] text-grey-500">
          {groups.length} field{groups.length === 1 ? "" : "s"} we couldn’t read automatically
        </span>
      </div>
      <div className="overflow-hidden rounded-[5px] border border-red-200 bg-red-50 shadow-panel">
        {groups.map((group) => (
          <NeedsReviewRow
            key={group.groupKey}
            group={group}
            busy={busyGroupKeys.has(group.groupKey)}
            onFix={onFix}
            sourceLabel={sourceLabel}
            onFixLocation={onFixLocation}
            onDismiss={onDismiss}
          />
        ))}
      </div>
    </div>
  );
}

interface NeedsReviewRowProps {
  group: PolicyNeedsReviewGroup;
  busy: boolean;
  onFix: (group: PolicyNeedsReviewGroup, newValue: string) => void;
  sourceLabel?: string;
  onFixLocation?: (group: PolicyNeedsReviewGroup, locationId: number, newValue: string) => void;
  onDismiss?: (group: PolicyNeedsReviewGroup) => void;
}

// For a toggle, the raw extracted value is a normalized code ("1"/"0"/"true"/"false"), not prose —
// quoting it verbatim ('The document said "1"') means nothing to a reader. Describe it in plain
// language instead; anything that doesn't cleanly normalize (genuinely ambiguous free text) still
// gets quoted as before, since that IS informative.
function currentAnswerPhrase(group: PolicyNeedsReviewGroup): React.ReactNode {
  if (group.fieldType === "toggle") {
    const normalized = group.rawExtracted.trim().toLowerCase();
    if (normalized === "1" || normalized === "true") return "this was on";
    if (normalized === "0" || normalized === "false") return "this was off";
  }
  return <>“{group.rawExtracted}”</>;
}

/** The card's main sentence — branches on `citationUnverified`, since that case has no confirmed
 * source at all: saying "{sourceLabel} said X" would claim confidence we don't have. */
function NeedsReviewExplanation({ group, sourceLabel }: { group: PolicyNeedsReviewGroup; sourceLabel: string }): React.ReactElement {
  if (group.citationUnverified) {
    return (
      <p className="text-[13px] text-grey-700">
        We extracted <span className="font-semibold text-grey-900">{currentAnswerPhrase(group)}</span> for this field
        {group.citation.trim() ? (
          <>
            , but the source text it was based on — <span className="italic">“{group.citation}”</span> — doesn’t actually appear in{" "}
            {sourceLabel.toLowerCase()}
          </>
        ) : (
          <>, but couldn’t confirm where it came from in {sourceLabel.toLowerCase()}</>
        )}
        , so we can’t confirm it’s correct. Please verify or correct it.
      </p>
    );
  }
  return (
    <p className="text-[13px] text-grey-700">
      {sourceLabel} said <span className="font-semibold text-grey-900">{currentAnswerPhrase(group)}</span>: {group.reason}
    </p>
  );
}

function NeedsReviewRow({ group, busy, onFix, sourceLabel = "The document", onFixLocation, onDismiss }: NeedsReviewRowProps): React.ReactElement {
  const commit = (value: string): void => {
    const trimmed = value.trim();
    if (trimmed) onFix(group, trimmed);
  };

  const showBreakdown = group.scope === "locations" && group.locations.length > 0 && onFixLocation !== undefined;

  return (
    <div className="flex flex-col gap-2 border-b border-red-200 px-5 py-3.5 last:border-b-0">
      <div className="flex items-start gap-2.5">
        <FiAlertCircle className="mt-0.5 size-4 shrink-0 text-red-500" />
        <div className="flex min-w-0 flex-col">
          <span className="text-sm font-semibold text-grey-900">{group.label}</span>
          {group.policyTitle && <span className="text-[12px] text-grey-500">{group.policyTitle}</span>}
        </div>
      </div>

      <NeedsReviewExplanation group={group} sourceLabel={sourceLabel} />

      {/* Always shown (not just for locations scope) — "applies to the whole organization" is just
       * as much an answer to "which locations does this apply to" as a named list is. Full names are
       * dropped here when the breakdown below can list them instead, so they're not shown twice. */}
      <p className="text-[12px] text-grey-500">
        {group.scope === "org"
          ? "Applies to: the whole organization"
          : showBreakdown
            ? `Affects ${group.locations.length} location${group.locations.length === 1 ? "" : "s"}`
            : `Affects ${group.locations.length} location${group.locations.length === 1 ? "" : "s"}: ${group.locations.map((location) => location.locationName).join(", ")}`}
      </p>

      <div className="flex items-center gap-2">
        <NeedsReviewFixControl group={group} busy={busy} onCommit={commit} />
        {onDismiss && (
          <button
            type="button"
            disabled={busy}
            onClick={() => onDismiss(group)}
            title="Not useful — make this go away without fixing it"
            className="text-[12px] font-semibold text-grey-500 transition-colors hover:text-grey-900 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Dismiss
          </button>
        )}
      </div>

      {showBreakdown && onFixLocation && (
        <NeedsReviewLocationBreakdown group={group} busy={busy} onFixLocation={(locationId, value) => onFixLocation(group, locationId, value)} />
      )}
    </div>
  );
}

interface NeedsReviewFixControlProps {
  group: PolicyNeedsReviewGroup;
  busy: boolean;
  onCommit: (value: string) => void;
}

/** The right control for `group.fieldType` — shared by the card's own bulk fix and, per-row, by
 * NeedsReviewLocationBreakdown below. */
function NeedsReviewFixControl({ group, busy, onCommit }: NeedsReviewFixControlProps): React.ReactElement {
  if (group.fieldType === "toggle") {
    return (
      <NeedsReviewSelect
        busy={busy}
        options={
          group.options && group.options.length > 0
            ? group.options
            : [
                { optionKey: "1", label: "Yes" },
                { optionKey: "0", label: "No" }
              ]
        }
        onChoose={onCommit}
      />
    );
  }
  if (group.fieldType === "single-select" && group.options && group.options.length > 0) {
    return <NeedsReviewSelect busy={busy} options={group.options} onChoose={onCommit} />;
  }
  if (group.fieldType === "multi-select" && group.options && group.options.length > 0) {
    return <NeedsReviewCheckboxes busy={busy} options={group.options} onCommit={onCommit} />;
  }
  return <NeedsReviewTextFix busy={busy} onCommit={onCommit} />;
}

interface NeedsReviewTextFixProps {
  busy: boolean;
  onCommit: (value: string) => void;
}

function NeedsReviewTextFix({ busy, onCommit }: NeedsReviewTextFixProps): React.ReactElement {
  const [draft, setDraft] = useState("");

  const commit = (): void => {
    const trimmed = draft.trim();
    if (trimmed) onCommit(trimmed);
  };

  return (
    <>
      <input
        value={draft}
        disabled={busy}
        placeholder="Type the correct value…"
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") commit();
        }}
        className="h-8 w-64 rounded-[4px] border border-grey-300 bg-white px-2.5 text-[13px] outline-none focus:border-orange-400"
      />
      <button
        type="button"
        disabled={busy || draft.trim() === ""}
        onClick={commit}
        className="rounded-[4px] border border-orange-200 bg-orange-50 px-2.5 py-1 text-[12px] font-semibold text-orange-700 transition-colors hover:bg-orange-100 disabled:cursor-not-allowed disabled:opacity-50"
      >
        Fix
      </button>
    </>
  );
}

interface NeedsReviewLocationBreakdownProps {
  group: PolicyNeedsReviewGroup;
  busy: boolean;
  onFixLocation: (locationId: number, value: string) => void;
}

/** Expandable "which locations, and fix each independently" detail — collapsed by default since a
 * card can cover a large share of the org's locations. The bulk control above stays the fast path
 * for "same corrected value for everyone"; this is for when a location needs a different one, or the
 * operator just wants to see the real list instead of a name count. */
function NeedsReviewLocationBreakdown({ group, busy, onFixLocation }: NeedsReviewLocationBreakdownProps): React.ReactElement {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="flex flex-col gap-1.5">
      <button type="button" onClick={() => setExpanded((v) => !v)} className="self-start text-[12px] font-semibold text-orange-700 hover:underline">
        {expanded ? "Hide" : "Show"} {group.locations.length} location{group.locations.length === 1 ? "" : "s"}
      </button>
      {expanded && (
        <div className="overflow-hidden rounded-[4px] border border-red-200 bg-white">
          {group.locations.map((location) => (
            <div
              key={location.locationId}
              className="flex flex-wrap items-center justify-between gap-2 border-b border-red-100 px-3 py-2 last:border-b-0"
            >
              <span className="truncate text-[12px] text-grey-700">{location.locationName}</span>
              <div className="flex items-center gap-2">
                <NeedsReviewFixControl group={group} busy={busy} onCommit={(value) => onFixLocation(location.locationId, value)} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface NeedsReviewSelectProps {
  busy: boolean;
  options: PolicyFieldOption[];
  onChoose: (value: string) => void;
}

function NeedsReviewSelect({ busy, options, onChoose }: NeedsReviewSelectProps): React.ReactElement {
  return (
    <select
      disabled={busy}
      defaultValue=""
      onChange={(event) => {
        if (event.target.value) onChoose(event.target.value);
      }}
      className="h-8 rounded-[4px] border border-grey-300 bg-white px-2.5 text-[13px] outline-none focus:border-orange-400"
    >
      <option value="" disabled>
        Choose the right value…
      </option>
      {options.map((option) => (
        <option key={option.optionKey} value={option.optionKey}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

interface NeedsReviewCheckboxesProps {
  busy: boolean;
  options: PolicyFieldOption[];
  onCommit: (value: string) => void;
}

function NeedsReviewCheckboxes({ busy, options, onCommit }: NeedsReviewCheckboxesProps): React.ReactElement {
  const [checked, setChecked] = useState<Set<string>>(new Set());

  const toggle = (optionKey: string): void => {
    const next = new Set(checked);
    if (next.has(optionKey)) next.delete(optionKey);
    else next.add(optionKey);
    setChecked(next);
  };

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex flex-wrap items-center gap-2.5">
        {options.map((option) => (
          <label key={option.optionKey} className="flex items-center gap-1.5 text-[13px] text-grey-700">
            <input type="checkbox" disabled={busy} checked={checked.has(option.optionKey)} onChange={() => toggle(option.optionKey)} />
            {option.label}
          </label>
        ))}
      </div>
      <button
        type="button"
        disabled={busy || checked.size === 0}
        onClick={() => onCommit([...checked].join(","))}
        className="rounded-[4px] border border-orange-200 bg-orange-50 px-2.5 py-1 text-[12px] font-semibold text-orange-700 transition-colors hover:bg-orange-100 disabled:cursor-not-allowed disabled:opacity-50"
      >
        Fix
      </button>
    </div>
  );
}
